import categories from "../data/categories"
import colors from "../data/colors"

export const getWaffleChartInitialPosition = (ARRAY_SIZE, BOX_SIZE, MARGIN) => {
  return new Array(ARRAY_SIZE).fill([]).map((_, i) =>
    new Array(ARRAY_SIZE).fill("dummy-value").map((__, j) => ({
      x: (BOX_SIZE + MARGIN * 2) * j,
      y: (BOX_SIZE + MARGIN * 2) * i,
    }))
  )
}

export const getWaffleChartColors = (ARRAY_SIZE, startCategory) => {
  return new Array(ARRAY_SIZE)
    .fill([])
    .map((_) => new Array(10).fill(colors[startCategory]))
}

export const NAME = "name",
  ABV = "abv",
  VOLUME = "volume"

export const SHAKEN = "Shaken",
  STIRRED = "Stirred",
  BUILT_IN_ICE = "Built in glass"

export const SERVE_UP = "Up",
  ON_THE_ROCKS = "On the rocks",
  CRUSHED_ICE = "Crushed ice"

export const INITIAL_INGREDIENTS = {
  name: "",
  volume: undefined,
  abv: undefined,
}
export const INITIAL_MIX_AND_SERVE = {
  mix: "",
  serve: "",
}

export const calculateFinalABV = (ingredients, mixAndServe) => {
  if (!ingredients.length || !mixAndServe.mix) return 0
  const volumeOfAllIngredients = ingredients.reduce(
    (sum, curr) => sum + curr.volume,
    0
  )
  //console.log({ volumeOfAllIngredients })

  const waterMapping = (mix, serve) => {
    if (mix === BUILT_IN_ICE && serve === ON_THE_ROCKS) return 0.2
    if (mix === BUILT_IN_ICE && serve === SERVE_UP) return 0
    if (mix === BUILT_IN_ICE && serve === CRUSHED_ICE) return 0.25
    if (mix === STIRRED && serve === ON_THE_ROCKS) return 0.3
    if (mix === STIRRED && serve === SERVE_UP) return 0.25
    if (mix === STIRRED && serve === CRUSHED_ICE) return 0.35
    if (mix === SHAKEN && serve === ON_THE_ROCKS) return 0.35
    if (mix === SHAKEN && serve === SERVE_UP) return 0.3
    if (mix === SHAKEN && serve === CRUSHED_ICE) return 0.4
  }
  const waterMixRatio = waterMapping(mixAndServe.mix, mixAndServe.serve)
  const waterVolume = volumeOfAllIngredients * waterMixRatio
  // console.log({ waterMixRatio })
  // console.log({ waterVolume })

  const totalVolume = volumeOfAllIngredients + waterVolume
  //console.log({ totalVolume })

  const ABVSumOfIngredients = ingredients.reduce(
    (tempAVB, curr) => tempAVB + curr.volume * (curr.abv / 100),
    0
  )
  //console.log({ ABVSumOfIngredients })

  const finalABV = (ABVSumOfIngredients / totalVolume) * 100
  if (finalABV % 1 !== 0) return finalABV.toFixed(1)
  return Math.round(finalABV)
}

// Get percentage values that add up to 100
// For the waffle chart
// https://gist.github.com/hijonathan/e597addcc327c9bd017c
const getRoudedValuesThatAddUpToDesiredSum = (values, desiredSum) => {
  let sum = 0
  let valueParts = values.map((value, i) => {
    // Get rounded down integer values.
    let integerValue = value | 0
    sum += integerValue
    return {
      integer: integerValue, // Integer part of the value
      decimal: value % 1, // Decimal part of the value
      originalIndex: i, // Used to return values in original order.
    }
  })
  if (sum !== desiredSum) {
    // Sort values by decimal part
    valueParts = valueParts.sort((a, b) => b.decimal - a.decimal)

    const diff = desiredSum - sum
    let i = 0

    // Distribute the difference.
    while (i < diff) {
      valueParts[i] && valueParts[i].integer++
      i++
    }
  }

  const originalOrder = valueParts.sort(
    (a, b) => a.originalIndex - b.originalIndex
  )
  return originalOrder.map((d) => d.integer)
}

export const calculatePercentAndColor = (ingredients) => {
  const volumeOfAllIngredients = ingredients.reduce(
    (sum, curr) => sum + curr.volume,
    0
  )

  const dataWithDecimalPercent = ingredients.map((d, i) => ({
    ...d,
    id: i + 1,
    color: colors[d.category]
      ? colors[d.category]
      : colors[categories.nonalchoholic],
    percent: (d.volume / volumeOfAllIngredients) * 100,
  }))

  const decimalPercents = dataWithDecimalPercent.map((d) => d.percent)
  const roundedPercents = getRoudedValuesThatAddUpToDesiredSum(
    decimalPercents,
    100
  )

  const dataWithRoundedPercents = dataWithDecimalPercent.map((d, i) => ({
    ...d,
    percent: roundedPercents[i],
  }))
  return dataWithRoundedPercents
}

export const formatIngredient = (d) => {
  const volume = d.volume ? Number(d.volume) : 1
  const abv = d.abv ? Number(d.abv) : 0
  const hex = d.hex ? `#${d.hex}` : "#000000"
  // const category = ""
  return {
    ...d,
    abv,
    volume,
    hex,
    // category,
  }
}

export const formatIngredients = (ingredients) =>
  ingredients.map((d) => formatIngredient(d))

export const getBoundingCoordinates = (ref, padding) => {
  const elem = ref?.current
  const topDifference = padding ? padding : 0
  if (elem) {
    const box = elem.getBoundingClientRect()
    const { top, bottom, left, right, height, width } = {
      top: box.top + window.pageYOffset - topDifference,
      right: box.right + window.pageXOffset,
      bottom: box.bottom + window.pageYOffset - topDifference,
      left: box.left + window.pageXOffset,
      width: box.width,
      height: box.height,
    }
    const coords = {
      topMid: [left + width / 2, top],
      bottomMid: [left + width / 2, bottom],
      leftMid: [left, top + height / 2],
      rightMid: [right, top + height / 2],
      center: [left + width / 2, top + height / 2],
      topRight: [right, top],
      topLeft: [left, top],
      bottomRight: [right, bottom],
      bottomLeft: [left, bottom],
    }
    return coords
  }
}

export const getScrollPosition = (ref, padding) => {
  const topPadding = padding ? padding : 0
  if (ref) {
    return ref.current.getBoundingClientRect().top + topPadding
  }
}

export const removeEmptyIngredients = (data) =>
  data.filter((ingr) => ingr.name && ingr.volume && ingr.abv !== null)
